import { Progress, Typography, theme } from 'antd';
import React from 'react';

import { CloudUploadIcon as CloudUploadOutlined } from '@/icons';

const { Title, Text } = Typography;

const PHASE_MESSAGES: Record<string, string> = {
  'knowledge.upload_received': 'Receiving your upload...',
  'knowledge.extracting_text': 'Extracting text from your document...',
  'knowledge.analyzing_context': 'Analyzing document context...',
  'knowledge.generating_summary': 'Generating AI summary...',
  'knowledge.loading_sharepoint': 'Saving to SharePoint...',
  'knowledge.completed': 'Document uploaded and indexed successfully.',
};

export interface AssetUploadProgressProps {
  /** Current phase key emitted by the backend (e.g. "knowledge.analyzing_context"). */
  phase?: string | null;
  /** 0–100 percentage from the SSE event. */
  progress?: number | null;
  /** When set, renders an error state with this message. */
  error?: string | null;
  /** Optional filename shown as the subtitle below the heading. */
  filename?: string;
}

/** Centered upload progress matching the step-generating layout of the Transcript Analysis screen. */
export const AssetUploadProgress: React.FC<AssetUploadProgressProps> = ({
  phase,
  progress,
  error,
  filename,
}) => {
  const { token } = theme.useToken();
  const isComplete = phase === 'knowledge.completed' || progress === 100;
  const hasError = Boolean(error);
  const displayProgress = isComplete ? 100 : (progress ?? 0);
  const phaseMessage = (phase ? PHASE_MESSAGES[phase] : null) ?? 'Processing...';
  const strokeColor = isComplete || hasError ? undefined : token.colorPrimary;

  let progressStatus: 'active' | 'success' | 'exception';
  if (hasError) {
    progressStatus = 'exception';
  } else if (isComplete) {
    progressStatus = 'success';
  } else {
    progressStatus = 'active';
  }

  return (
    <div className="step-generating__centered">
      <div
        className="step-generating__icon-ring"
        style={hasError ? { background: 'rgba(255, 77, 79, 0.1)' } : undefined}
      >
        <CloudUploadOutlined
          style={{
            fontSize: 32,
            color: hasError ? '#ff4d4f' : token.colorPrimary,
          }}
        />
      </div>

      <Title level={3} style={{ margin: 0 }}>
        Uploading Document
      </Title>

      {filename ? (
        <Text type="secondary" className="step-generating__subtitle">
          {filename}
        </Text>
      ) : null}

      <Progress
        percent={displayProgress}
        status={progressStatus}
        strokeColor={strokeColor}
        showInfo={false}
        style={{ maxWidth: 400, width: '100%' }}
        aria-label="Document upload progress"
      />

      {hasError ? (
        <Text type="danger" className="step-generating__phase-label">
          {error}
        </Text>
      ) : (
        <Text type="secondary" className="step-generating__phase-label">
          {phaseMessage}
        </Text>
      )}
    </div>
  );
};

export default AssetUploadProgress;
